import { FontService, HttpService, MeshService } from '@models/services';
import { MaterialService } from '../services/material-service';
import { AnnotationNode, DimensionNode } from './annotations';
import {
    BaseConnector,
    CircularPatternFeature,
    DeleteFacesFeature,
    DisplacementMapFeature,
    ExtrudeFeature,
    Feature,
    JoinGeometryFeature,
    MoveVerticesFeature,
    NormalSmoothingFeature,
    SweepFeature,
    TransformFeature,
    UvMapFeature,
    WeldVerticesFeature,
} from './features';
import { Connector } from './features/connector';
import { MirrorFeature } from './features/mirror-feature';
import { PatternFeature } from './features/pattern-feature';
import { SketchControlPoint } from './features/sketch-control-point';
import { SliceFeature } from './features/slice-feature';
import { SubmeshFeature } from './features/submesh-feature';
import { IImportInfo } from './kb3d-models';
import { Kb3dObject } from './kb3d-object';
import { DirectionalLightNode, HemisphericLightNode, LightNode, PointLightNode, SpotLightNode } from './lights';
import {
    AdvancedMaterialNode,
    FileTextureLayer,
    MaterialNode,
    SvgTextureLayer,
    TextTextureLayer,
    TextureLayer,
} from './materials';
import { ColorFillTextureLayer } from './materials/color-fill-texture-layer';
import { MateNode } from './mates/mate-node';
import {
    BoxNode,
    CustomMeshNode,
    CylinderNode,
    DiscNode,
    MeshGroupNode,
    MeshNode,
    PlaneNode,
    PrimitiveMeshNode,
    SpaceNode,
    SphereNode,
    TorusNode,
} from './meshes';
import { ConeNode } from './meshes/cone-node';
import { SketchNode } from './meshes/sketch-node';
import { SketchPath } from './meshes/sketch-node-path';
import { TextNode } from './meshes/text-node';
import { KbColor, KbGeometry, KbQuaternion, KbVector } from './primitives';
import { SceneNode } from './scene-node';
import { ViewpointNode } from './viewpoints/viewpoint-node';

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export class Token<T = any> {
    protected constructor(public key: string) {}

    public static withKey(key: keyof typeof Token): Token {
        const token = this[key] as Token;
        if (!token) throw new Error(`Could not find token for key '${key}'`);
        return token;
    }

    public static tryWithKey(key: keyof typeof Token): Token | undefined {
        const token = this[key] as Token;
        return token;
    }

    /** Services */
    public static HttpService = new Token<HttpService>('HttpService');
    public static MaterialService = new Token<MaterialService>('MaterialService');
    public static MeshService = new Token<MeshService>('MeshService');
    public static JobService = new Token<any>('JobService');
    public static SceneService = new Token<any>('SceneService');
    public static FontService = new Token<FontService>('FontService');
    public static Ignore = new Token('Ignore');

    /** Roots */
    public static SceneNode = new Token<SceneNode>('SceneNode');
    public static Kb3dObject = new Token<Kb3dObject>('Kb3dObject');
    public static ImportInfo = new Token<IImportInfo>('ImportInfo');

    /** Lights */
    public static LightNode = new Token<LightNode>('LightNode');
    public static DirectionalLightNode = new Token<DirectionalLightNode>('DirectionalLightNode');
    public static HemisphericLightNode = new Token<HemisphericLightNode>('HemisphericLightNode');
    public static PointLightNode = new Token<PointLightNode>('PointLightNode');
    public static SpotLightNode = new Token<SpotLightNode>('SpotLightNode');

    /** Meshes */
    public static BoxNode = new Token<BoxNode>('BoxNode');
    public static CustomMeshNode = new Token<CustomMeshNode>('CustomMeshNode');
    public static CylinderNode = new Token<CylinderNode>('CylinderNode');
    public static ConeNode = new Token<ConeNode>('ConeNode');
    public static DiscNode = new Token<DiscNode>('DiscNode');
    public static PlaneNode = new Token<PlaneNode>('PlaneNode');
    public static SphereNode = new Token<SphereNode>('SphereNode');
    public static TorusNode = new Token<TorusNode>('TorusNode');
    public static MeshNode = new Token<MeshNode>('MeshNode');
    public static PrimitiveMeshNode = new Token<PrimitiveMeshNode>('PrimitiveMeshNode');
    public static MeshGroupNode = new Token<MeshGroupNode>('MeshGroupNode');
    public static SpaceNode = new Token<SpaceNode>('SpaceNode');
    public static TextNode = new Token<TextNode>('TextNode');
    public static SketchNode = new Token<SketchNode>('SketchNode');
    public static SketchPath = new Token<SketchPath>('SketchPath');

    /** Dimensions */
    public static AnnotationNode = new Token<AnnotationNode>('AnnotationNode');
    public static DimensionNode = new Token<DimensionNode>('DimensionNode');

    /** Primitives */
    public static KbColor = new Token<KbColor>('KbColor');
    // public static KbColor4 = new Token<KbColor4>("KbColor4");
    public static KbGeometry = new Token<KbGeometry>('KbGeometry');
    public static KbQuaternion = new Token<KbQuaternion>('KbQuaternion');
    public static KbVector = new Token<KbVector>('KbVector');

    /** Materials */
    public static MaterialNode = new Token<MaterialNode>('MaterialNode');
    public static AdvancedMaterialNode = new Token<AdvancedMaterialNode>('AdvancedMaterialNode');
    public static MaterialMapping = new Token<TextureLayer>('MaterialMapping');

    /** Textures */
    public static TextureLayer = new Token<TextureLayer>('TextureLayer');
    public static FileTextureLayer = new Token<FileTextureLayer>('FileTextureLayer');
    public static SvgTextureLayer = new Token<SvgTextureLayer>('SvgTextureLayer');
    public static TextTextureLayer = new Token<TextTextureLayer>('TextTextureLayer');
    public static ColorFillTextureLayer = new Token<ColorFillTextureLayer>('ColorFillTextureLayer');

    /** Viewpoints */
    public static ViewpointNode = new Token<ViewpointNode>('ViewpointNode');

    /** Features */
    public static Feature = new Token<Feature>('Feature');
    public static BaseConnector = new Token<BaseConnector>('BaseConnector');
    public static Connector = new Token<Connector>('Connector');
    public static SketchControlPoint = new Token<SketchControlPoint>('SketchControlPoint');
    public static SubmeshFeature = new Token<SubmeshFeature>('SubmeshFeature');
    public static MoveVerticesFeature = new Token<MoveVerticesFeature>('MoveVerticesFeature');
    public static WeldVerticesFeature = new Token<WeldVerticesFeature>('WeldVerticesFeature');
    public static DisplacementMapFeature = new Token<DisplacementMapFeature>('DisplacementMapFeature');
    public static UvMapFeature = new Token<UvMapFeature>('UvMapFeature');
    public static LinearPatternFeature = new Token<PatternFeature>('LinearPatternFeature');
    public static CircularPatternFeature = new Token<CircularPatternFeature>('CircularPatternFeature');
    public static JoinGeometryFeature = new Token<JoinGeometryFeature>('JoinGeometryFeature');
    public static DeleteFacesFeature = new Token<DeleteFacesFeature>('DeleteFacesFeature');
    public static NormalSmoothingFeature = new Token<NormalSmoothingFeature>('NormalSmoothingFeature');
    public static SliceFeature = new Token<SliceFeature>('SliceFeature');
    public static MirrorFeature = new Token<MirrorFeature>('MirrorFeature');
    public static TransformFeature = new Token<TransformFeature>('TransformFeature');
    public static ExtrudeFeature = new Token<ExtrudeFeature>('ExtrudeFeature');
    public static SweepFeature = new Token<SweepFeature>('SweepFeature');

    public static MateNode = new Token<MateNode>('MateNode');

    protected static MapFrom(...tokens: Token[]) {
        return new Map(tokens.map(t => [t, null]));
    }

    public static PrimitiveTokens = [Token.KbVector, Token.KbQuaternion, Token.KbColor];
    public static PrimitiveTokenMap = Token.MapFrom(...Token.PrimitiveTokens);

    public static PrimitiveMeshTokens = [
        Token.BoxNode,
        Token.CylinderNode,
        Token.ConeNode,
        Token.DiscNode,
        Token.PlaneNode,
        Token.SphereNode,
        Token.TorusNode,
    ];
    public static PrimitiveMeshTokenMap = Token.MapFrom(...Token.PrimitiveMeshTokens);

    public static MeshTokens = Token.PrimitiveMeshTokens.concat(Token.CustomMeshNode, Token.SketchNode, Token.TextNode);
    public static MeshTokenMap = Token.MapFrom(...Token.MeshTokens);

    public static MeshAndGroupTokens = Token.MeshTokens.concat(Token.MeshGroupNode);
    public static MeshAndGroupTokenMap = Token.MapFrom(...Token.MeshAndGroupTokens);

    public static SpaceTokens = Token.MeshAndGroupTokens.concat(Token.SceneNode);
    public static SpaceTokenMap = Token.MapFrom(...Token.SpaceTokens);

    public static MaterialTokens = [Token.AdvancedMaterialNode];

    public static MaterialTokenMap = Token.MapFrom(...Token.MaterialTokens);

    public static AnnotationTokens = [Token.AnnotationNode, Token.DimensionNode];

    public static AnnotationTokenMap = Token.MapFrom(...Token.AnnotationTokens);

    public static PositionalLightTokens = [Token.PointLightNode, Token.SpotLightNode];
    public static GlobalLightTokens = [Token.DirectionalLightNode, Token.HemisphericLightNode];
    public static LightTokens = [...Token.GlobalLightTokens, ...Token.PositionalLightTokens];
    public static LightTokenMap = Token.MapFrom(...Token.LightTokens);

    public static FeatureTokens = [
        Token.MoveVerticesFeature,
        Token.WeldVerticesFeature,
        Token.DisplacementMapFeature,
        Token.UvMapFeature,
        Token.SubmeshFeature,
        Token.LinearPatternFeature,
        Token.CircularPatternFeature,
        Token.JoinGeometryFeature,
        Token.DeleteFacesFeature,
        Token.NormalSmoothingFeature,
        Token.SliceFeature,
        Token.MirrorFeature,
        Token.TransformFeature,
        Token.ExtrudeFeature,
        Token.SweepFeature,
    ];
    public static FeatureTokenMap = Token.MapFrom(...Token.FeatureTokens);

    public static ConnectorTokens = [Token.Connector, Token.SketchControlPoint];

    public static TextureLayerTokens = [
        Token.TextureLayer,
        Token.FileTextureLayer,
        Token.SvgTextureLayer,
        Token.TextTextureLayer,
        Token.ColorFillTextureLayer,
    ];
    public static TextureLayerTokenMap = Token.MapFrom(...Token.TextureLayerTokens);

    public static NodeTokens = [
        ...Token.SpaceTokens,
        ...Token.MaterialTokens,
        ...Token.LightTokens,
        ...Token.AnnotationTokens,
        Token.ViewpointNode,
        Token.MateNode,
    ];
    public static NodeTokenMap = Token.MapFrom(...Token.NodeTokens);
    public static BaseNodeTokens = [
        Token.SpaceNode,
        Token.MaterialNode,
        Token.LightNode,
        Token.ViewpointNode,
        Token.MateNode,
        Token.AnnotationNode,
    ];

    public static AllTokens = Token.NodeTokens.concat(
        ...Token.PrimitiveTokens,
        Token.FeatureTokens,
        Token.TextureLayerTokens,
        Token.ConnectorTokens,
        [Token.SketchPath]
    );
    public static AllTokenMap = Token.MapFrom(...Token.AllTokens);

    public isPrimitive(): boolean {
        return Token.PrimitiveTokenMap.has(this);
    }
    public isPrimitiveMesh(): boolean {
        return Token.PrimitiveMeshTokenMap.has(this);
    }
    public isMesh(): boolean {
        return Token.MeshTokenMap.has(this);
    }
    public isMeshOrGroup(): boolean {
        return Token.MeshAndGroupTokenMap.has(this);
    }
    public isMaterial(): boolean {
        return Token.MaterialTokenMap.has(this);
    }
    public isFeature(): boolean {
        return Token.FeatureTokenMap.has(this);
    }
    public isTexture(): boolean {
        return Token.TextureLayerTokenMap.has(this);
    }
    public isLight(): boolean {
        return Token.LightTokenMap.has(this);
    }
    public isViewpoint(): boolean {
        return Token.ViewpointNode === this;
    }
    public isMate(): boolean {
        return Token.MateNode === this;
    }
    public isConnector(): boolean {
        return Token.Connector === this || Token.SketchControlPoint === this;
    }
    public isAnnotation(): boolean {
        return Token.AnnotationTokenMap.has(this);
    }
    public isNode(): boolean {
        return Token.NodeTokenMap.has(this);
    }
    public isSceneNode(): boolean {
        return Token.SceneNode === this;
    }
    public isSketchPath(): boolean {
        return Token.SketchPath === this;
    }
}
